import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { logCreation } from '@/lib/activityLog';
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Vendor } from "@/types";
import { vendorSchema } from "@/lib/validation";
import { ErrorHandler } from "@/lib/errorHandler";
import { getBalanceDisplay } from "@/lib/balanceUtils";
import { useFormSubmitLock } from "@/hooks/useFormSubmitLock";
import { useVendorGroups } from "@/hooks/useGroupingQueries";
import { ModalHeader } from "@/components/window-manager/ModalHeader";

type VendorFormData = z.infer<typeof vendorSchema>;

interface VendorModalProps {
  open: boolean;
  isMinimized?: boolean;
  onOpenChange: (open: boolean) => void;
  onMinimize?: () => void;
  onClose?: () => void;
  vendor?: Vendor;
  organizationId: string | null;
  onSuccess: () => void;
  windowId?: string;
}

export function VendorModal({ open, isMinimized = false, onOpenChange, onMinimize, onClose, vendor, organizationId, onSuccess, windowId }: VendorModalProps) {
  const { isSubmitting, withSubmitLock } = useFormSubmitLock();
  const { data: vendorGroups } = useVendorGroups();
  
  // Track which windowId this form was initialized for - prevents data loss on minimize/restore
  const initializedWindowIdRef = useRef<string | null>(null);
  
  const form = useForm<VendorFormData>({
    resolver: zodResolver(vendorSchema),
    defaultValues: {
      name: "",
      company_name: "",
      phone: "",
      email: "",
      address: "",
      opening_balance: 0,
      balance_type: 'payable',
      notes: "",
      group_id: "",
    },
  });

  useEffect(() => {
    // Skip if minimized - prevents re-initialization on restore
    if (isMinimized) return;
    // Skip if already initialized for this specific window
    if (windowId && initializedWindowIdRef.current === windowId) return;
    
    if (vendor) {
      form.reset({
        name: vendor.name,
        company_name: vendor.company_name || "",
        phone: vendor.phone || "",
        email: vendor.email || "",
        address: vendor.address || "",
        opening_balance: vendor.opening_balance,
        balance_type: (vendor as any).balance_type || 'payable',
        notes: vendor.notes || "",
        group_id: (vendor as any).group_id || "",
      });
    } else if (open) {
      form.reset({
        name: "",
        company_name: "",
        phone: "",
        email: "",
        address: "",
        opening_balance: 0,
        balance_type: 'payable',
        notes: "",
        group_id: "",
      });
    }
    
    if (windowId) {
      initializedWindowIdRef.current = windowId;
    }
  }, [vendor, open, form, windowId, isMinimized]);

  const onSubmit = withSubmitLock(async (data: VendorFormData) => {
    if (!organizationId) {
      toast.error("Organization not found");
      return;
    }

    try {
      // Convert "none" to null for group_id
      const groupId = data.group_id === "none" || !data.group_id ? null : data.group_id;
      
      if (vendor) {
        // Update existing vendor
        const { error } = await supabase
          .from('vendors')
          .update({
            name: data.name,
            company_name: data.company_name || null,
            phone: data.phone || null,
            email: data.email || null,
            address: data.address || null,
            notes: data.notes || null,
            group_id: groupId,
          })
          .eq('id', vendor.id);

        if (error) throw error;
        toast.success('Vendor updated successfully');
      } else {
        // For new vendors, adjust opening balance based on balance_type
        let adjustedBalance = data.opening_balance || 0;
        if (data.balance_type === 'receivable') {
          // Vendor owes us - balance should be positive
          adjustedBalance = Math.abs(adjustedBalance);
        } else {
          // We owe vendor - balance should be negative
          adjustedBalance = -Math.abs(adjustedBalance);
        }

        // Create new vendor
        const { data: newVendor, error: insertError } = await supabase
          .from('vendors')
          .insert({
            name: data.name,
            company_name: data.company_name || null,
            phone: data.phone || null,
            email: data.email || null,
            address: data.address || null,
            opening_balance: adjustedBalance,
            current_balance: adjustedBalance,
            balance_type: data.balance_type,
            notes: data.notes || null,
            group_id: groupId,
            organization_id: organizationId,
          })
          .select()
          .single();

        if (insertError) throw insertError;

        if (newVendor) {
          await logCreation(organizationId, 'vendor', newVendor.id, newVendor.name);
        }

        // Create opening balance ledger entry if there's an opening balance
        if (adjustedBalance !== 0 && newVendor) {
          const { error: ledgerError } = await supabase
            .from('ledger_entries')
            .insert({
              organization_id: organizationId,
              party_id: newVendor.id,
              party_type: 'vendor',
              transaction_type: 'opening_balance',
              date: new Date().toISOString().split('T')[0],
              debit: adjustedBalance > 0 ? adjustedBalance : 0,
              credit: adjustedBalance < 0 ? Math.abs(adjustedBalance) : 0,
              balance: adjustedBalance,
              description: 'Opening Balance',
            });

          if (ledgerError) {
            console.error('Error creating opening balance entry:', ledgerError);
            toast.error('Failed to create opening balance ledger entry. Vendor creation rolled back.');
            // Delete the vendor if ledger entry failed to maintain data integrity
            await supabase.from('vendors').delete().eq('id', newVendor.id);
            throw ledgerError;
          }
        }

        toast.success('Vendor created successfully');
      }

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      ErrorHandler.handle(error, vendor ? 'Updating Vendor' : 'Creating Vendor');
    }
  });

  // Handle minimize - keep in taskbar
  const handleMinimize = () => {
    if (onMinimize) {
      onMinimize();
    } else {
      onOpenChange(false);
    }
  };

  // Handle close - remove from taskbar
  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} modal={!isMinimized} onOpenChange={(openState) => !openState && handleMinimize()}>
      <DialogContent 
        className="max-w-2xl max-h-[90vh] overflow-y-auto [&>button.absolute]:hidden"
        isMinimized={isMinimized}
        onInteractOutside={(e) => {
          e.preventDefault();
          handleMinimize();
        }}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
          handleMinimize();
        }}
      >
        <ModalHeader
          title={vendor ? 'Edit Vendor' : 'Add Vendor'}
          onMinimize={handleMinimize}
          onClose={handleClose}
        />
        <DialogDescription>
          {vendor ? 'Update vendor information' : 'Enter the details of the new vendor'}
        </DialogDescription>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter vendor name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="company_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Company name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input placeholder="+92 300 1234567" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="vendor@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Enter vendor address" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="group_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vendor Group</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || ""}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select group (optional)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {vendorGroups?.map((group: any) => (
                        <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {!vendor && (
              <>
                <FormField
                  control={form.control}
                  name="opening_balance"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Opening Balance (PKR)</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          step="0.01"
                          placeholder="0" 
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="balance_type"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel>Balance Type</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          className="flex flex-col space-y-1"
                        >
                          <FormItem className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value="payable" />
                            </FormControl>
                            <FormLabel className="font-normal cursor-pointer flex items-center gap-2">
                              <span>We Owe Vendor</span>
                              <Badge variant="outline" className="bg-[#ff4d4d]/10 border-[#ff4d4d]/30">
                                <span className="text-[#ff4d4d] font-semibold">Payable</span>
                              </Badge>
                            </FormLabel>
                          </FormItem>
                          <FormItem className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value="receivable" />
                            </FormControl>
                            <FormLabel className="font-normal cursor-pointer flex items-center gap-2">
                              <span>Vendor Owes Us</span>
                              <Badge variant="outline" className="bg-[#00c46a]/10 border-[#00c46a]/30">
                                <span className="text-[#00c46a] font-semibold">Receivable</span>
                              </Badge>
                            </FormLabel>
                          </FormItem>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            {vendor && (
              <div className="p-4 bg-muted/50 rounded-lg border border-border">
                <div className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-muted-foreground">Current Balance:</span>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-foreground">
                      PKR {Math.abs(vendor.current_balance || 0).toLocaleString()}
                    </span>
                    <Badge 
                      variant={getBalanceDisplay(vendor.current_balance || 0, 'vendor').isPositive ? "default" : "destructive"}
                      className={getBalanceDisplay(vendor.current_balance || 0, 'vendor').bgColor}
                    >
                      <span className={`${getBalanceDisplay(vendor.current_balance || 0, 'vendor').textColor} font-semibold`}>
                        {getBalanceDisplay(vendor.current_balance || 0, 'vendor').label}
                      </span>
                    </Badge>
                  </div>
                </div>
              </div>
            )}

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Additional notes about the vendor" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : (vendor ? 'Update' : 'Create')}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
